import React, { useEffect } from "react";
import {
  FaEnvelope,
  FaVideo,
  FaCalendar,
  FaFile,
  FaCloud,
  FaLock,
  FaSearch,
  FaMobile,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import {
  SiGmail,
  SiGooglemeet,
  SiGooglecalendar,
  SiGoogledocs,
  SiGoogledrive,
  SiGooglesheets,
  SiGoogleslides,
} from "react-icons/si";
import AOS from "aos";
import "aos/dist/aos.css";

// Google 아이콘 이미지 임포트 (2026 리브랜딩)
import gmailIcon    from "../../images/google/Google-Workspace-logos/Gmail/logo_gmail_2026q2_color_2x_web_48dp.png";
import meetIcon     from "../../images/google/Google-Workspace-logos/Meet/logo_meet_2026q2_color_2x_web_48dp.png";
import calendarIcon from "../../images/google/Google-Workspace-logos/Calendar/logo_calendar_2026q2_color_2x_web_48dp.png";
import docsIcon     from "../../images/google/Google-Workspace-logos/Docs/logo_docs_2026q2_color_2x_web_48dp.png";
import sheetsIcon   from "../../images/google/Google-Workspace-logos/Sheets/logo_sheets_2026q2_color_2x_web_48dp.png";
import slidesIcon   from "../../images/google/Google-Workspace-logos/Slides/logo_slides_2026q2_color_2x_web_48dp.png";
import driveIcon    from "../../images/google/Google-Workspace-logos/Drive/logo_drive_2026q2_color_2x_web_48dp.png";

const Features = () => {
  useEffect(() => {
    // AOS 애니메이션 초기화
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
    });
  }, []);

  // 색상 테마 설정
  const colorThemes = {
    red: { light: "bg-red-50", medium: "text-red-500", dark: "bg-red-500" },
    blue: { light: "bg-blue-50", medium: "text-blue-500", dark: "bg-blue-500" },
    green: {
      light: "bg-green-50",
      medium: "text-green-500",
      dark: "bg-green-500",
    },
    yellow: {
      light: "bg-yellow-50",
      medium: "text-yellow-500",
      dark: "bg-yellow-500",
    },
    indigo: {
      light: "bg-indigo-50",
      medium: "text-indigo-500",
      dark: "bg-indigo-500",
    },
    purple: {
      light: "bg-purple-50",
      medium: "text-purple-500",
      dark: "bg-purple-500",
    },
    teal: { light: "bg-teal-50", medium: "text-teal-500", dark: "bg-teal-500" },
    orange: {
      light: "bg-orange-50",
      medium: "text-orange-500",
      dark: "bg-orange-500",
    },
  };

  const features = [
    {
      icon: <img src={gmailIcon} alt="Gmail" className="w-8 h-8" />,
      color: "red",
      title: "Gmail for Business",
      description: "기업용 이메일 서비스",
      details: [
        "도메인 기반의 전문적인 이메일 주소",
        "스팸 및 악성코드 차단",
        "메일 보관 및 복구 기능",
        "통합 검색 시스템",
      ],
    },
    {
      icon: <img src={meetIcon} alt="Google Meet" className="w-8 h-8" />,
      color: "blue",
      title: "Google Meet",
      description: "화상 회의 솔루션",
      details: [
        "최대 250명 동시 접속",
        "화면 공유 및 실시간 자막",
        "회의 녹화 기능",
        "캘린더 연동",
      ],
    },
    {
      icon: (
        <img src={calendarIcon} alt="Google Calendar" className="w-8 h-8" />
      ),
      color: "teal",
      title: "Google Calendar",
      description: "일정 관리 도구",
      details: [
        "팀 일정 공유",
        "회의실 예약 시스템",
        "외부 캘린더 연동",
        "자동 일정 조율",
      ],
    },
    {
      icon: (
        <div className="flex space-x-1">
          <img src={docsIcon} alt="Google Docs" className="w-6 h-6" />
          <img src={sheetsIcon} alt="Google Sheets" className="w-6 h-6" />
          <img src={slidesIcon} alt="Google Slides" className="w-6 h-6" />
        </div>
      ),
      color: "green",
      title: "Google Workspace",
      description: "문서 작성 도구",
      details: [
        "Docs, Sheets, Slides",
        "실시간 공동 작업",
        "자동 저장",
        "버전 관리",
      ],
    },
    {
      icon: <img src={driveIcon} alt="Google Drive" className="w-8 h-8" />,
      color: "yellow",
      title: "Google Drive",
      description: "클라우드 스토리지",
      details: [
        "무제한 스토리지",
        "파일 공유 및 권한 관리",
        "오프라인 동기화",
        "강력한 검색 기능",
      ],
    },
    {
      icon: <FaLock className="w-8 h-8" />,
      color: "purple",
      title: "보안 기능",
      description: "기업 데이터 보호",
      details: ["2단계 인증", "데이터 암호화", "모바일 기기 관리", "감사 로그"],
    },
    {
      icon: <FaSearch className="w-8 h-8" />,
      color: "indigo",
      title: "통합 검색",
      description: "기업 데이터 검색",
      details: [
        "전체 콘텐츠 검색",
        "실시간 검색 결과",
        "고급 필터링",
        "보안 설정 반영",
      ],
    },
    {
      icon: <FaMobile className="w-8 h-8" />,
      color: "orange",
      title: "모바일 지원",
      description: "모바일 업무 환경",
      details: [
        "모바일 앱 제공",
        "크로스 플랫폼 지원",
        "실시간 동기화",
        "보안 정책 적용",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* 히어로 섹션 */}
      <div className="relative bg-gradient-to-r from-blue-700 to-blue-900 text-white overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern
                id="grid-pattern"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="white"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-blue-800/50 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <div className="flex space-x-1 mr-3">
                {[
                  <img src={gmailIcon} alt="Gmail"  className="w-5 h-5 text-red-400" key="gmail"  />,
                  <img src={meetIcon} alt="Gmail"  className="w-5 h-5 text-blue-400" key="meet"  />,
                  <img src={driveIcon} alt="Gmail"  className="w-5 h-5 text-yellow-400" key="drive"  />,               
                ]}
              </div>
              <span className="text-sm font-medium">구글 워크스페이스</span>
            </div>

            <h1
              className="text-4xl md:text-5xl font-bold mb-6  text-white"
              data-aos="fade-up"
            >
              비즈니스 생산성을 높이는
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-100">
                Google Workspace 핵심 기능
              </span>
            </h1>

            <p
              className="text-xl text-blue-100 max-w-3xl mx-auto mb-10"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              기업의 효율적인 협업과 커뮤니케이션을 지원하는 Google Workspace의
              주요 기능들을 소개합니다.
            </p>

            <a
              href="#features"
              className="inline-flex items-center bg-white/10 hover:bg-white/20 text-white rounded-full px-6 py-3 transition-colors backdrop-blur-sm border border-white/20"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              자세히 알아보기
              <FaArrowRight className="ml-2 text-sm" />
            </a>
          </div>
        </div>

        {/* 물결 모양 디바이더 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            fill="#ffffff"
          >
            <path d="M0,96L48,85.3C96,75,192,53,288,53.3C384,53,480,75,576,80C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      {/* 기능 소개 섹션 */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const colorSet = colorThemes[feature.color];

              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                  data-aos="fade-up"
                  data-aos-delay={index * 50}
                >
                  {/* 상단 컬러 바 */}
                  <div className={`h-1.5 ${colorSet.dark}`}></div>

                  <div className="p-6">
                    {/* 아이콘 */}
                    <div
                      className={`w-14 h-14 ${colorSet.light} rounded-lg flex items-center justify-center mb-5 ${colorSet.medium}`}
                    >
                      {feature.icon}
                    </div>

                    {/* 제목 및 설명 */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p
                      className={`${colorSet.medium} text-sm font-medium mb-4`}
                    >
                      {feature.description}
                    </p>

                    {/* 상세 기능 목록 */}
                    <ul className="space-y-3">
                      {feature.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start">
                          <FaCheckCircle
                            className={`${colorSet.medium} mt-1 mr-2 flex-shrink-0`}
                            size={14}
                          />
                          <span className="text-gray-600 text-sm">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 통합 서비스 섹션 */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl overflow-hidden shadow-xl"
            data-aos="fade-up"
          >
            <div className="md:flex">
              <div className="md:w-1/2 p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  모든 도구가 하나로 연결됩니다
                </h2>
                <p className="text-gray-600 mb-6">
                  Google Workspace는 이메일, 문서 작성, 화상 회의 등 모든 업무
                  도구가 유기적으로 연결되어 있어 원활한 업무 흐름을 제공합니다.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5">
                      <FaCheckCircle className="text-blue-600" size={14} />
                    </div>
                    <span>Gmail에서 바로 Meet 화상회의 생성</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5">
                      <FaCheckCircle className="text-blue-600" size={14} />
                    </div>
                    <span>Calendar와 Meet의 완벽한 통합</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5">
                      <FaCheckCircle className="text-blue-600" size={14} />
                    </div>
                    <span>Drive에서 모든 파일 중앙 관리</span>
                  </li>
                </ul>
                <a
                  href="http://pf.kakao.com/_jyebn/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center"
                >
                  <button className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md flex items-center">
                    통합 솔루션 체험하기
                    <FaArrowRight className="ml-2 text-sm" />
                  </button>
                </a>
              </div>
              <div className="md:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-600 p-8 md:p-12 flex items-center justify-center">
                <div className="grid grid-cols-3 gap-3 max-w-xs">
                  {[
                    {
                      icon: <img src={gmailIcon} alt="Gmail" className="w-5 h-5" />,
                      name: "Gmail",
                      color: "bg-white text-red-500",
                    },
                    {
                      icon: <img src={meetIcon} alt="Meet" className="w-5 h-5" />,
                      name: "Meet",
                      color: "bg-white text-blue-500",
                    },
                    {
                      icon: <img src={calendarIcon} alt="Calendar" className="w-5 h-5" />,
                      name: "Calendar",
                      color: "bg-white text-teal-500",
                    },
                    {
                      icon: <img src={docsIcon} alt="Docs" className="w-5 h-5" />,
                      name: "Docs",
                      color: "bg-white text-blue-600",
                    },
                    {
                      icon: <img src={sheetsIcon} alt="Sheets" className="w-5 h-5" />,
                      name: "Sheets",
                      color: "bg-white text-green-600",
                    },
                    {
                      icon: <img src={slidesIcon} alt="Slides" className="w-5 h-5" />,
                      name: "Slides",
                      color: "bg-white text-yellow-600",
                    },
                    {
                      icon: <img src={driveIcon} alt="Drive" className="w-5 h-5" />,
                      name: "Drive",
                      color: "bg-white text-yellow-500",
                    },
                    {
                      icon: <FaLock />,
                      name: "Security",
                      color: "bg-white text-purple-500",
                    },
                    {
                      icon: <FaMobile />,
                      name: "Mobile",
                      color: "bg-white text-orange-500",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`${item.color} rounded-lg p-3 text-center shadow-lg`}
                    >
                      <div className="text-xl mb-1 flex justify-center">
                        {item.icon}
                      </div>
                      <div className="text-xs font-medium">{item.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl" data-aos="fade-up">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl overflow-hidden shadow-2xl relative">
            {/* 배경 패턴 */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%">
                <defs>
                  <pattern
                    id="dots"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle cx="10" cy="10" r="2" fill="white" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#dots)" />
              </svg>
            </div>

            <div className="p-10 md:p-16 text-center relative z-10">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
                <span className="text-white text-sm font-medium">
                  30일 무료 체험
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                지금 바로 Google Workspace를 무료로 시작해보세요
              </h2>

              <p className="text-white text-lg mb-10 max-w-2xl mx-auto">
                30일 동안 모든 기능을 제한 없이 사용해볼 수 있습니다. 전문가의
                기술 지원과 함께 비즈니스에 최적화된 환경을 경험하세요.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="http://pf.kakao.com/_jyebn/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <button className="w-full sm:w-auto bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300 shadow-lg">
                    무료 체험 시작하기
                  </button>
                </a>
                <a
                  href="http://pf.kakao.com/_jyebn/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <button className="w-full sm:w-auto bg-transparent text-white px-8 py-4 rounded-lg font-semibold border-2 border-white hover:bg-white/10 transition-colors duration-300">
                    전문가에게 문의하기
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
